import { useDebounce } from "hooks/useDebounce"
import { useEffect, useRef, useState } from "react"
import { useFormContext } from "react-hook-form"

interface SearchMultiSelectDropdownProps {
  useSearchHook: (searchTerm: string) => any
  placeholder: string
  label: string
  itemType: string
  fieldName: string
}

export function SearchMultiSelectDropdown({
  useSearchHook,
  placeholder,
  label,
  itemType,
  fieldName
}: SearchMultiSelectDropdownProps) {
  const { watch, setValue } = useFormContext()
  const selected = watch(fieldName) ?? []

  const [search, setSearch] = useState("")
  const [isOpen, setIsOpen] = useState(false)

  const debouncedSearch = useDebounce(search, 400)
  const { data, isFetching } = useSearchHook(debouncedSearch)

  const selectedIds = new Set(selected.map((item: any) => item.id || item))
  const containerRef = useRef<HTMLDivElement>(null)

  const handleSelect = (item: any) => {
    const newItem = {
      id: item.id,
      name: item.name || item.title,
      thumbnail: item.thumbnail,
      type: itemType
    }
    setValue(fieldName, [...selected, newItem], { shouldDirty: true })
    setSearch("")
    setIsOpen(false)
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const results = data?.results ?? []

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <input
        value={search}
        onChange={e => {
          setSearch(e.target.value)
          setIsOpen(true)
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 dark:bg-slate-700 dark:text-white dark:focus:border-blue-500 dark:hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      {isOpen && isFetching && (
        <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm text-gray-500 dark:text-gray-400 shadow-lg">
          Searching...
        </div>
      )}

      {isOpen &&
        !isFetching &&
        debouncedSearch.trim().length > 0 &&
        results.length === 0 && (
          <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-sm text-gray-500 dark:text-gray-400 shadow-lg">
            No results found
          </div>
        )}

      {isOpen && results.length > 0 && (
        <div
          className="
            absolute z-20 mt-1 w-full
            bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 rounded
            max-h-60 overflow-y-auto
            shadow-lg
          "
        >
          {results.map((item: any) => {
            const isSelected = selectedIds.has(item.id)

            return (
              <button
                key={item.id}
                type="button"
                disabled={isSelected}
                onClick={() => handleSelect(item)}
                className="
                  flex w-full gap-3 px-3 py-2 text-left
                  hover:bg-gray-100 dark:hover:bg-slate-700
                  disabled:opacity-50
                  disabled:cursor-not-allowed
                "
              >
                {item.thumbnail && (
                  <img
                    src={item.thumbnail}
                    className="w-8 h-8 rounded object-cover"
                    alt={item.name || item.title}
                  />
                )}
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {item.name || item.title}
                  </p>
                  {item.type && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.type}</p>
                  )}
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
